import type { Event, EventHint, Integration } from '@sentry/core';
import { isSentryErrorLike } from './SentryErrorIntegration';

// CloudFront answers a WAF block itself (the per-IP rate limit, #64975)
// and names itself in `Server`. A 403 from PHP arrives with the origin's
// own `Server` header, so that header tells the two apart.
export function isCloudFrontBlock(status: number, server: string | null | undefined): boolean {
   return (
      status === 403 && typeof server === 'string' && server.trim().toLowerCase() === 'cloudfront'
   );
}

type ResponseLike = {
   status_code?: unknown;
   headers?: unknown;
};

function responseIsCloudFrontBlock(response: unknown): boolean {
   if (typeof response !== 'object' || response === null) {
      return false;
   }
   const { status_code, headers } = response as ResponseLike;
   if (typeof status_code !== 'number' || typeof headers !== 'object' || headers === null) {
      return false;
   }
   // `Headers.forEach` hands out lowercase keys, so the key is `server`.
   const { server } = headers as Record<string, unknown>;
   return isCloudFrontBlock(status_code, typeof server === 'string' ? server : null);
}

// Reads the response off the thrown error as well as the event, so it does
// not depend on running after sentryErrorIntegration has merged the two.
export function ignoreCloudFrontBlockIntegration(): Integration {
   return {
      name: 'ignoreCloudFrontBlockIntegration',
      processEvent(event: Event, hint: EventHint) {
         const exception = hint.originalException;
         const thrown = isSentryErrorLike(exception)
            ? exception.sentryDetails.contexts?.response
            : undefined;
         if (
            responseIsCloudFrontBlock(thrown) ||
            responseIsCloudFrontBlock(event.contexts?.response)
         ) {
            return null;
         }
         return event;
      },
   };
}
